<template>
	<MediaCaptionDialog ref="form" v-model="showNotesPanel" v-model:caption="snapshotNotes" v-model:validity="isFormValid"
		:saving="savingNotes" @cancel="close" @save="saveNotes" @after-leave="resetAfterLeave" />
</template>

<script lang="ts" setup>
	import { computed, ref, nextTick, useTemplateRef } from "vue";
	import api from "../api";
	import { calculateTimeout, getMessageFromError } from "@/helpers/utilities";
	import { useToast } from "vue-toastification";
	import MediaCaptionDialog from "@common-ui/media/MediaCaptionDialog.vue";
	import { countCodePoints } from "@common-ui/utilities/characterCount";

	const props = withDefaults(
		defineProps<{
			notes?: string;
			currentSnapshotId: string | null;
		}>(),
		{ notes: "" }
	);

	const emit = defineEmits<{
		updatedNotes: [payload: string];
	}>();

	const NOTES_MAX_LENGTH = 200;

	const showNotesPanel = ref(false);
	const snapshotNotes = ref(props.notes);
	const savingNotes = ref(false);
	const form = useTemplateRef<InstanceType<typeof MediaCaptionDialog>>("form");
	const isFormValid = ref<boolean | null>(null);
	const toast = useToast();
	const isNotesDirty = computed(() => {
		return snapshotNotes.value !== props.notes;
	});

	// LSP-17265: counted in UNTRIMMED code points, matching the AppTextarea counter above (which uses
	// the app-wide default unit, deliberately not overridden here) and the Snapshots.notes varchar(1400)
	// column, which counts code points. 200 code points is well inside that column.
	// NOTE: contact-api's snapshots.ts guards this field at 5,000 GRAPHEMES, which is both the wrong
	// unit and larger than the column — a latent 22001 for any client that isn't this 200-char box.
	// Tracked as LSP-17541; not this PR's to fix.
	const canSaveNotes = computed(() => {
		return !savingNotes.value && !!isFormValid.value && isNotesDirty.value && countCodePoints(snapshotNotes.value) <= NOTES_MAX_LENGTH;
	});

	async function saveNotes() {
		// CLAUDE.md: action buttons stay enabled; the click handler guards on validity instead of
		// `:disabled` (LSP-16465, LSP-17265 review Finding B). canSaveNotes remains the single source
		// of truth for whether a save is currently allowed.
		if (!canSaveNotes.value) {
			console.warn("SnapshotNotes.saveNotes: blocked — canSaveNotes is false, not saving", {
				snapshotId: props.currentSnapshotId,
				savingNotes: savingNotes.value,
				isFormValid: isFormValid.value,
				isNotesDirty: isNotesDirty.value
			});
			return;
		}
		try {
			// Same silent-failure rule as the canSaveNotes gate above: log WHICH condition stood the save
			// down, so a "my caption didn't save" report is triageable from logs alone. break-it review.
			if (!props.currentSnapshotId || !form.value) {
				console.warn("SnapshotNotes.saveNotes: blocked — missing snapshot id or form ref, not saving", {
					snapshotId: props.currentSnapshotId ?? null,
					hasFormRef: !!form.value
				});
				return;
			}
			savingNotes.value = true;
			const { valid, errors } = await form.value.validate();
			if (!valid) {
				const errorMessage = errors.flatMap((error) => error.errorMessages).join(". ") || "Please fix the errors before saving.";
				toast.error(errorMessage, { timeout: calculateTimeout(errorMessage) });
				return;
			}
			const request = { notes: snapshotNotes.value };
			await api.updateSnapshot(props.currentSnapshotId, request);
			emit("updatedNotes", snapshotNotes.value);
			close();
		} catch (e: unknown) {
			console.error(e, {
				tags: {
					method: "saveNotes",
					file: "SnapshotNotes"
				},
				extra: {
					snapshotId: props.currentSnapshotId,
					notes: snapshotNotes.value
				}
			});
			const errorMessage = getMessageFromError(e) || "An error occurred while saving the notes. Please try again.";
			toast.error(errorMessage, {
				timeout: calculateTimeout(errorMessage)
			});
		} finally {
			savingNotes.value = false;
		}
	}

	function open() {
		showNotesPanel.value = true;
		nextTick(() => {
			snapshotNotes.value = props.notes;
		});
	}

	function close() {
		showNotesPanel.value = false;
	}

	function resetAfterLeave() {
		form.value?.resetValidation();
		savingNotes.value = false;
	}

	defineExpose({ open, close });
</script>
